import type { FeedItem, RecentLiker } from '@/types/feed';

/** Minimal current-user shape needed to build an optimistic liker entry. */
export interface OptimisticLikeUser {
  id: string;
  username?: string | null;
  name?: string | null;
  image?: string | null;
}

/** The subset of FeedItem fields affected when the current user toggles their like. */
export type OptimisticLikeFields = Pick<
  FeedItem,
  'userHasLiked' | 'likesCount' | 'recentLikers'
>;

/**
 * Pure recompute of a FeedItem's like fields after the current user toggles their like.
 * `currentlyLiked` is the state BEFORE the toggle. Immutable: never mutates `item`.
 *
 * Shared by the React Query optimistic cache update (use-like-check-in) and the
 * ActivityCard's local optimistic view state, so both stay in exact lockstep.
 */
export function applyOptimisticLike(
  item: FeedItem,
  currentUser: OptimisticLikeUser | null,
  currentlyLiked: boolean,
): OptimisticLikeFields {
  const currentUserLiker: RecentLiker | null = currentUser
    ? {
        id: currentUser.id,
        username: currentUser.username,
        displayName: currentUser.name,
        avatarUrl: currentUser.image,
      }
    : null;

  let recentLikers = [...item.recentLikers];
  if (currentlyLiked) {
    // Unliking: remove the current user from recent likers.
    recentLikers = recentLikers.filter((l) => l.id !== currentUser?.id);
  } else if (currentUserLiker) {
    // Liking: prepend the current user (deduped), capped at 3.
    recentLikers = [
      currentUserLiker,
      ...recentLikers.filter((l) => l.id !== currentUser?.id),
    ].slice(0, 3);
  }

  return {
    userHasLiked: !currentlyLiked,
    likesCount: currentlyLiked
      ? Math.max(0, item.likesCount - 1)
      : item.likesCount + 1,
    recentLikers,
  };
}
